import { hasRegion, RegionTable } from '@deighton/shared-ui/core';

/** The region cookie. `__Host-` pins it to this exact host: Secure, Path=/, no Domain. */
export const REGION_COOKIE_NAME: string = '__Host-dtims_region';

/**
 * Full-page path that forgets the saved region and shows the picker.
 * Settings → Switch region navigates here before Angular boots.
 */
export const SELECT_REGION_PATH: string = '/select-region';

/** Legacy same-origin region hosts: `<region>.dtims.app`. */
const LEGACY_REGION_HOST_PATTERN: RegExp = /^([a-z0-9]+)\.dtims\.app$/;

/** The region the shell talks to for this page load. */
export interface ShellRegion {
  /** Key in the region allowlist, e.g. `we`. */
  key: string;

  /** Display name, e.g. `EMEA`. */
  label: string;

  /** API base URL. Empty on a legacy region host: API calls are same-origin there. */
  apiBaseUrl: string;

  /** How it was resolved. Only `cookie` and `picker` regions can be switched. */
  source: RegionSource;
}

/** How the region was resolved: legacy host, cookie, picker, or the dev build's base URL. */
export type RegionSource = 'host' | 'cookie' | 'picker' | 'build';

/** What the browser tells the resolver about this page load. */
export interface RegionResolverInput {
  /** `location.hostname`. */
  hostname: string;

  /** `location.pathname`. */
  pathname: string;

  /** `location.search`, including the leading `?`. */
  search: string;

  /** `document.cookie`. */
  cookie: string;
}

/** Outcome of {@link resolveRegion}. */
export type RegionResolution =
  { kind: 'region'; region: ShellRegion } | { kind: 'picker'; clearCookie: boolean };

/**
 * Picks the region for this page load. The first rule that matches wins:
 * a. host `<r>.dtims.app` with r in regions → r, same-origin API (legacy host)
 * b. path `/select-region` → clear the cookie, show the picker
 * c. region cookie in regions → that region; any other value → clear it, show the picker
 * d. otherwise → the picker
 *
 * A key read from the host or the cookie is only used to look up `regions`; every URL comes
 * from the table, never from input.
 * @param input - Location and cookie of the current page.
 * @param regions - The region allowlist from the runtime config.
 * @returns The resolved region, or a request to show the picker.
 */
export function resolveRegion(input: RegionResolverInput, regions: RegionTable): RegionResolution {
  const hostMatch: RegExpMatchArray | null = input.hostname.toLowerCase().match(LEGACY_REGION_HOST_PATTERN);
  if (hostMatch && hasRegion(regions, hostMatch[1])) {
    return { kind: 'region', region: toRegion(regions, hostMatch[1], '', 'host') };
  }

  if (isSelectRegionPath(input.pathname)) {
    return { kind: 'picker', clearCookie: true };
  }

  const cookieValue: string | null = readCookie(input.cookie, REGION_COOKIE_NAME);
  if (cookieValue !== null) {
    if (hasRegion(regions, cookieValue)) {
      return {
        kind: 'region',
        region: toRegion(regions, cookieValue, regions[cookieValue].apiBaseUrl, 'cookie'),
      };
    }
    return { kind: 'picker', clearCookie: true };
  }

  return { kind: 'picker', clearCookie: false };
}

/**
 * Whether {@link pathname} is {@link SELECT_REGION_PATH}, ignoring one trailing slash.
 * @param pathname - `location.pathname`.
 * @returns True for `/select-region` and `/select-region/`.
 */
export function isSelectRegionPath(pathname: string): boolean {
  const normalized: string = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return normalized === SELECT_REGION_PATH;
}

/**
 * Builds the region from its allowlist entry.
 * @param regions - The region allowlist.
 * @param key - A key known to be in `regions`.
 * @param apiBaseUrl - The API base to use for this page load.
 * @param source - How the region was resolved.
 * @returns The shell region.
 */
export function toRegion(
  regions: RegionTable,
  key: string,
  apiBaseUrl: string,
  source: RegionSource
): ShellRegion {
  return Object.freeze({ key, label: regions[key].label, apiBaseUrl, source });
}

/**
 * Reads one cookie value from a `document.cookie` string.
 * @param cookieHeader - The `document.cookie` string.
 * @param name - Cookie name.
 * @returns The raw value, or null when the cookie is absent.
 */
export function readCookie(cookieHeader: string, name: string): string | null {
  for (const part of cookieHeader.split(';')) {
    const separator: number = part.indexOf('=');
    if (separator > -1 && part.slice(0, separator).trim() === name) {
      return part.slice(separator + 1).trim();
    }
  }
  return null;
}

/**
 * The `document.cookie` assignment that stores the chosen region for a year.
 * @param key - A configured region key.
 * @returns The cookie string.
 */
export function regionCookie(key: string): string {
  return `${REGION_COOKIE_NAME}=${key}; Secure; Path=/; SameSite=Lax; Max-Age=31536000`;
}

/**
 * The `document.cookie` assignment that deletes the region cookie.
 * @returns The cookie string.
 */
export function clearedRegionCookie(): string {
  return `${REGION_COOKIE_NAME}=; Secure; Path=/; SameSite=Lax; Max-Age=0`;
}

/** Time zone prefixes that preselect a region on the picker. A hint only, never a decision. */
const TIME_ZONE_HINTS: ReadonlyArray<[string, string]> = [
  ['Europe/', 'we'],
  ['Africa/', 'we'],
  ['Australia/', 'ae'],
  ['Pacific/', 'ae'],
];

/**
 * Suggests a region from the browser time zone.
 * @param timeZone - `Intl.DateTimeFormat().resolvedOptions().timeZone`.
 * @param regions - The region allowlist.
 * @returns A configured region key, or null when there is no hint.
 */
export function suggestRegion(timeZone: string | undefined, regions: RegionTable): string | null {
  if (!timeZone) {
    return null;
  }
  const hint: [string, string] | undefined = TIME_ZONE_HINTS.find(([prefix]: [string, string]): boolean =>
    timeZone.startsWith(prefix)
  );
  return hint && hasRegion(regions, hint[1]) ? hint[1] : null;
}
